
import React from "react";
import { AdminUser } from "@/hooks/admin/users/types";
import { UserRole } from "@/types/strategy-types";
import UserTableRow from "./UserTableRow";
import ProviderDetailsRow from "./ProviderDetailsRow";

interface UserRowGroupProps {
  user: AdminUser;
  expandedUsers: string[];
  editingUser: string | null;
  editFirstName: string;
  editLastName: string;
  onToggleExpand: (userId: string) => void;
  onEditFirstNameChange: (value: string) => void;
  onEditLastNameChange: (value: string) => void;
  onStartEdit: (user: AdminUser) => void;
  onCancelEdit: () => void;
  onSaveEdit: (userId: string) => void;
  onUpdateRole: (roleId: string, newRole: UserRole) => Promise<void>;
  onUpdateName: (userId: string, firstName: string, lastName: string) => Promise<void>;
  onDeleteUser: (userId: string) => void;
  getProviderStrategies: (providerId: string | undefined) => any[];
  getAssignedProvider: (providerId: string | undefined) => any | null;
}

/**
 * A component that renders a user row and its related expanded details if applicable
 * This avoids using React.Fragment directly which was causing warnings with data-lov-id
 */
const UserRowGroup: React.FC<UserRowGroupProps> = ({
  user,
  expandedUsers,
  editingUser,
  editFirstName,
  editLastName,
  onToggleExpand,
  onEditFirstNameChange,
  onEditLastNameChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onUpdateRole,
  onUpdateName,
  onDeleteUser,
  getProviderStrategies,
  getAssignedProvider
}) => {
  const isExpanded = expandedUsers.includes(user.id);
  const isProvider = user.role === UserRole.StrategyProvider;
  
  return (
    <>
      <UserTableRow 
        user={user}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        editingUser={editingUser}
        editFirstName={editFirstName}
        editLastName={editLastName}
        onEditFirstNameChange={onEditFirstNameChange}
        onEditLastNameChange={onEditLastNameChange}
        onStartEdit={onStartEdit}
        onCancelEdit={onCancelEdit}
        onSaveEdit={onSaveEdit}
        onUpdateRole={onUpdateRole}
        onUpdateName={onUpdateName}
        onDeleteUser={onDeleteUser}
      />
      
      {isExpanded && isProvider && (
        <ProviderDetailsRow 
          user={user}
          getProviderStrategies={getProviderStrategies}
          getAssignedProvider={getAssignedProvider}
        />
      )}
    </>
  );
};

export default UserRowGroup;
